import { useCallback, useEffect, useState } from 'react';

import {
  getCachedConsent,
  getCookieFirstConsent,
  hasCookieFirstScriptConfigured,
  openCookiePreferencesPanel,
  subscribeToConsentCache,
  type CookieFirstCategory,
  type CookieFirstConsent,
} from 'utils/cookieFirst';

interface UseCookieConsentResult {
  consent: CookieFirstConsent | null;
  isConfigured: boolean;
  isReady: boolean;
  isCategoryAccepted: (category: CookieFirstCategory) => boolean;
  openPanel: () => void;
}

const useCookieConsent = (): UseCookieConsentResult => {
  const isConfigured = hasCookieFirstScriptConfigured();
  const [consent, setConsent] = useState<CookieFirstConsent | null>(() => getCachedConsent());
  const [isReady, setIsReady] = useState<boolean>(() => typeof window !== 'undefined' && Boolean(window.CookieFirst));

  useEffect(() => {
    if (!isConfigured) {
      return undefined;
    }

    setConsent(getCachedConsent() ?? getCookieFirstConsent());
    setIsReady(Boolean(window.CookieFirst));

    return subscribeToConsentCache((nextConsent) => {
      setConsent(nextConsent);
      setIsReady(Boolean(window.CookieFirst));
    });
  }, [isConfigured]);

  const isCategoryAccepted = useCallback(
    (category: CookieFirstCategory) => {
      if (!consent) {
        return false;
      }

      return consent[category] === true;
    },
    [consent]
  );

  return {
    consent,
    isConfigured,
    isReady,
    isCategoryAccepted,
    openPanel: openCookiePreferencesPanel,
  };
};

export default useCookieConsent;
